import { Controller } from 'stimulus';
import createActivityDetector from 'activity-detector';
import { t } from '../javascript/lib/i18n';

const LOCAL_STORAGE_KEY_PREFIX = 'rpm-session-time';

const CLASS_BELOW_TIMER_TARGET = 'text-warning';
const CLASS_ABOVE_TIMER_TARGET = 'text-success';

const BORDER_CLASS_BELOW_TIMER_TARGET = ['border', 'border-warning'];
const BORDER_CLASS_ABOVE_TIMER_TARGET = ['border', 'border-success'];

const STALE_STORAGE_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour
const ACTIVITY_DETECTOR_TIME_TO_IDLE_SECONDS = 600; // 10 minutes
const ACTIVITY_DETECTOR_CONFIG = {
  timeToIdle: ACTIVITY_DETECTOR_TIME_TO_IDLE_SECONDS * 1000, // Required in ms
  ignoredEventsWhenIdle: [], // Defaults to `['mousemove']` which we don't want to ignore
  inactivityEvents: [], // Ignore all inactivity events that force a change from `active` to `idle`, we only want the timer
};

export default class extends Controller {
  static targets = ['displayTimer', 'container'];

  static values = {
    showTimer: Boolean,
    vipId: Number,
    userId: Number,
    recommendedTime: Number,
    recommendedTimeEnabled: Boolean,
    timeElapsed: Number,
    pauseTimer: Boolean,
  };

  connect() {
    this.addRpmFormEventListener();

    if (this.pauseTimerValue) {
      return;
    }

    this.storage = JSON.parse(localStorage.getItem(this.storageKey));

    if (this.showTimerValue) {
      this.setTimerColour();

      if (this.isStorageStale()) {
        this.resetTimer();
        this.storage = null;
      }

      if (this.storage?.vipId === this.vipIdValue) {
        if (this.storage.time) {
          this.timeElapsedValue = this.storage.time;
        }

        this.setupDisplayTimer();

        /**
         * The `disconnect` method will automatically handle turbo visits.
         *
         * This `beforeunload` event will catch all other page changes:
         *   - Hard refreshes
         *   - Hard page navigation
         *   - Closing the tab/browser
         */
        window.addEventListener(
          'beforeunload',
          () => {
            this.disconnect();
          },
          { once: true }
        );
      } else {
        this.resetTimer();
        this.removeStorage();
        this.setupDisplayTimer();
      }

      this.initActivityDetector();
    } else {
      this.resetTimer();
      this.removeStorage();
    }
  }

  disconnect() {
    if (this.activityDetector) {
      this.activityDetector.stop();
    }

    this.clearDisplayInterval();
  }

  initActivityDetector() {
    this.activityDetector = createActivityDetector(ACTIVITY_DETECTOR_CONFIG);
    this.setupActivityDetectorEvents();
  }

  setupActivityDetectorEvents() {
    this.activityDetector.on('idle', this.pauseTimer);

    this.activityDetector.on('active', this.startTimer);
  }

  startTimer = () => {
    this.displayTimerInterval = setInterval(this.runTimer, 1000);
  };

  runTimer = () => {
    this.updateDisplayTimer();
    this.updateLocalStorage();
  };

  pauseTimer = () => {
    clearInterval(this.displayTimerInterval);
  };

  setupDisplayTimer() {
    this.currentDisplayTime = this.timeElapsedValue || 0;
    this.updateDisplayTimer();
    this.startTimer();

    // Store the full storage key on the html element so removeStorage()
    // can clean up correctly even when vipId/userId aren't available
    document.documentElement.dataset.currentRpmStorageKey = this.storageKey;
  }

  updateDisplayTimer = () => {
    if (!this.hasDisplayTimerTarget) {
      this.clearDisplayInterval();
      return;
    }

    this.currentDisplayTime++;

    this.currentTotal = this.getTimeElapsed();
    this.displayTimerTarget.textContent = `${this.currentTotal.minutes}:${this.currentTotal.seconds}`;

    this.setTimerColour();
  };

  resetTimer() {
    this.clearDisplayInterval();

    this.timeElapsedValue = 0;
    this.currentDisplayTime = 0;

    localStorage.removeItem(this.storageKey);
  }

  resetAndRestartTimer() {
    if (!confirm(t('js.rpm_session_timer.confirm_reset'))) {
      return;
    }

    this.resetTimer();
    this.displayTimerTarget.textContent = '00:00';
    this.startTimer();
    document.documentElement.dataset.currentRpmStorageKey = this.storageKey;
  }

  clearDisplayInterval() {
    if (this.displayTimerInterval) {
      clearInterval(this.displayTimerInterval);
      this.displayTimerInterval = null;
    }
  }

  getTimeElapsed() {
    return {
      total: this.currentDisplayTime,
      minutes: Math.floor((this.currentDisplayTime % 3600) / 60)
        .toString()
        .padStart(2, '0'),
      seconds: Math.floor(this.currentDisplayTime % 60)
        .toString()
        .padStart(2, '0'),
    };
  }

  setTimerColour() {
    if (this.recommendedTimeEnabledValue) {
      if (
        this.currentTotal?.total &&
        this.currentTotal.total >= this.recommendedTimeValue
      ) {
        this.displayTimerTarget.classList.remove(CLASS_BELOW_TIMER_TARGET);
        this.containerTarget.classList.remove(
          ...BORDER_CLASS_BELOW_TIMER_TARGET
        );
        this.displayTimerTarget.classList.add(CLASS_ABOVE_TIMER_TARGET);
        this.containerTarget.classList.add(...BORDER_CLASS_ABOVE_TIMER_TARGET);
      } else {
        this.displayTimerTarget.classList.add(CLASS_BELOW_TIMER_TARGET);
        this.containerTarget.classList.add(...BORDER_CLASS_BELOW_TIMER_TARGET);
      }
    }
  }

  updateLocalStorage() {
    if (this.currentTotal) {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify({
          time: this.currentTotal.total,
          vipId: this.vipIdValue,
          lastUpdatedAt: Date.now(),
        })
      );
    }
  }

  isStorageStale() {
    if (!this.storage?.lastUpdatedAt) {
      return false;
    }

    return Date.now() - this.storage.lastUpdatedAt > STALE_STORAGE_THRESHOLD_MS;
  }

  // This is called when we're not on an RPM page (i.e there's no vipIDValue or showTimerValue passed), or if the time is considered stale
  // This'll remove the timer from storage and remove the attribute
  removeStorage() {
    const storageKey = document.documentElement.dataset.currentRpmStorageKey;

    if (storageKey) {
      localStorage.removeItem(storageKey);
      document.documentElement.removeAttribute('data-current-rpm-storage-key');
    }
  }

  addRpmFormEventListener() {
    window.addEventListener('rpmTimeSubmitted', (event) => {
      this.resetTimer();

      if (!event.target.dataset.endTimer) {
        this.setupDisplayTimer();
      }
    });
  }

  get storageKey() {
    return `${LOCAL_STORAGE_KEY_PREFIX}-${this.userIdValue}-${this.vipIdValue}`;
  }
}
